import * as React from 'react'

import { NetworkStatus } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import type { ErrorLike } from '@apollo/client'

import { toast } from '@buffer-mono/popcorn'

import { PostMediaAsset_Asset } from '~publish/components/PostCard/PostMedia/PostMediaAsset'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { ResultOf, VariablesOf } from '~publish/graphql'
import { useInfiniteScrollPagination } from '~publish/hooks/useInfiniteScrollPagination'

import { CommentData_Comment } from '../../Comments/fragments/commentData'
import {
  CommentLastReply_LastReply,
  CommentMediaAsset_Asset,
} from '../../Comments/fragments/commentLastReply'
import { getPublishedAtRange } from '../../CommunityFilters/getPublishedAtRange'
import { DEFAULT_SORT_OPTION } from '../../CommunityFilters/types'
import type {
  BaseCommunityFilters,
  SortOption,
} from '../../CommunityFilters/types'
import type { CommentStatus } from '../../graphqlEnums'

// Replaces all comments with fresh data (used when new comments arrive)
const replaceCommentsQuery = (
  prev: CommentsQuery,
  { fetchMoreResult }: { fetchMoreResult: CommentsQuery | null },
): CommentsQuery => {
  if (!fetchMoreResult) return prev
  return {
    comments: {
      ...fetchMoreResult.comments,
      edges: fetchMoreResult.comments.edges,
    },
  }
}

// Appends new comments to existing list (used for infinite scroll)
const appendCommentsQuery = (
  prev: CommentsQuery,
  { fetchMoreResult }: { fetchMoreResult: CommentsQuery | null },
): CommentsQuery => {
  if (!fetchMoreResult) return prev
  return {
    comments: {
      ...fetchMoreResult.comments,
      edges: [...prev.comments.edges, ...fetchMoreResult.comments.edges],
    },
  }
}

export const COMMENTS_PER_PAGE = 20

export const CommentsQueryGQL = graphql(
  `
    query Comments($input: CommentsInput!, $first: Int!, $after: String) {
      comments(input: $input, first: $first, after: $after) {
        edges {
          node {
            # The card tree consumes these nodes as CommentData, so the shape
            # comes from the fragment. The inline selection below repeats most
            # of it to add the Asset id: CommentData_Comment omits that field,
            # and without it Apollo stores the same asset under two identities.
            ...CommentData_Comment
            ... on BaseComment {
              __typename
              id
              text
              contentType
              publishedAt
              status
              parentId
              channelId
              serviceType
              isOwnReply
              organizationId
              publicUrl
              postPublishedAt
              metadata {
                ... on GoogleBusinessCommentMetadata {
                  userRating
                }
              }
              lastReplies {
                ...CommentLastReply_LastReply
              }
              assets {
                ...CommentMediaAsset_Asset
              }
              author {
                username
                name
                avatar
              }
              post {
                id
                text
                dueAt
                channelService
                externalLink
                assets {
                  ...PostMediaAsset_Asset
                  type
                }
                tags {
                  name
                  color
                }
                channel {
                  id
                  service
                  name
                  displayName
                }
              }
              isDeleted
              reactions {
                type
              }
            }
            ... on CommentFromThirdParty {
              labels
              suggestions {
                text
                source
              }
              sentimentAnalysis {
                type
              }
            }
            ... on CommentFromBuffer {
              publishingStatus {
                status
              }
            }
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          endCursor
        }
      }
    }
  `,
  [
    CommentLastReply_LastReply,
    CommentMediaAsset_Asset,
    PostMediaAsset_Asset,
    CommentData_Comment,
  ],
)

export type CommentsQuery = ResultOf<typeof CommentsQueryGQL>
export type CommentsQueryVariables = VariablesOf<typeof CommentsQueryGQL>

type UseCommentsQueryProps = {
  channelIds?: string[]
  filters?: Omit<BaseCommunityFilters, 'status'> & {
    status?: CommentStatus | 'all'
  }
  skip?: boolean
}

export type QueryVariables = CommentsQueryVariables

function sortOptionToGraphQL(
  sortOption: SortOption,
): CommentsQueryVariables['input']['sort'] {
  switch (sortOption) {
    case 'unansweredFirst':
      return [
        { field: 'status' as const, direction: 'desc' as const },
        { field: 'publishedAt' as const, direction: 'desc' as const },
      ]
    case 'oldest':
      return [{ field: 'publishedAt' as const, direction: 'asc' as const }]
    case 'newest':
    default:
      return [{ field: 'publishedAt' as const, direction: 'desc' as const }]
  }
}

/**
 * Custom hook that generates stable query variables for the GraphQL query.
 * Implements memoization to prevent unnecessary re-renders and query refetches.
 * Uses a stable key generation strategy for arrays by sorting and joining values.
 */
function useVariables(variables: {
  dateStart?: string
  organizationId: string | undefined | null
  channelIds?: string[]
  postId?: string
  status?: CommentStatus[]
  sortOption?: SortOption
}): QueryVariables {
  const {
    dateStart,
    organizationId,
    channelIds,
    postId,
    status,
    sortOption = DEFAULT_SORT_OPTION,
  } = variables

  const sort = sortOptionToGraphQL(sortOption)

  return React.useMemo(
    () =>
      ({
        input: {
          filters: {
            channelIds,
            status,
            publishedAt: {
              start: dateStart ?? undefined,
            },
            postId: postId ? [postId] : undefined,
          },
          sort,
          organizationId: organizationId ?? '',
        },

        first: COMMENTS_PER_PAGE,
        after: null,
      }) satisfies QueryVariables,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dateStart,
      organizationId,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [...(channelIds ?? [])]?.toSorted().join(','),
      postId,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      status?.join(','),
      sortOption,
    ],
  )
}

export function useCommentsQuery({
  channelIds,
  filters,
  skip,
}: UseCommentsQueryProps): {
  data: CommentsQuery | undefined
  loading: boolean
  error: ErrorLike | undefined
  fetchingMore: boolean
  lastElementRef: (node: Element | null) => void
  fetchNewComments: () => Promise<void>
  retry: () => Promise<void>
} {
  const organizationId = useOrganizationId()
  const { status, sortOption, publishedAt, postId } = filters ?? {}

  // Resolved once per publishedAt change; hour-stable so all consumers share
  // the same Apollo cache entry (see getPublishedAtRange).
  const { dateStart } = React.useMemo(
    () => getPublishedAtRange(publishedAt),
    [publishedAt],
  )

  const queryVariables = useVariables({
    dateStart,
    organizationId,
    channelIds,
    postId,
    status: status ? (status === 'all' ? undefined : [status]) : undefined,
    sortOption,
  })

  const { data, loading, error, fetchMore, networkStatus, refetch } = useQuery(
    CommentsQueryGQL,
    {
      variables: queryVariables,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      skip: !organizationId || skip,
    },
  )

  const fetchingMore = networkStatus === NetworkStatus.fetchMore

  const retry = React.useCallback(async (): Promise<void> => {
    try {
      await refetch()
    } catch {
      return
    }
  }, [refetch])

  // Build variables for fetchMore calls - only the cursor and merge strategy differ
  const buildFetchVariables = React.useCallback(
    (cursor: string | null): CommentsQueryVariables => ({
      input: {
        filters: {
          channelIds,
          postId: postId ? [postId] : undefined,
          status: status && status !== 'all' ? [status] : undefined,
          publishedAt: {
            start: dateStart ?? undefined,
          },
        },
        sort: sortOptionToGraphQL(sortOption ?? DEFAULT_SORT_OPTION),
        organizationId: organizationId ?? '',
      },
      first: COMMENTS_PER_PAGE,
      after: cursor,
    }),
    [channelIds, postId, status, dateStart, sortOption, organizationId],
  )

  const fetchNewComments = React.useCallback(async (): Promise<void> => {
    try {
      await fetchMore({
        variables: buildFetchVariables(null),
        updateQuery: replaceCommentsQuery,
      })
    } catch (fetchNewCommentsError) {
      toast.error('Error fetching new comments')
      throw new Error(
        'Failed to fetch new comments: ' +
          (fetchNewCommentsError instanceof Error
            ? fetchNewCommentsError
            : new Error(String(fetchNewCommentsError))
          ).message,
        { cause: fetchNewCommentsError },
      )
    }
  }, [buildFetchVariables, fetchMore])

  const [lastElementRef] = useInfiniteScrollPagination({
    loading: fetchingMore,
    hasNextPage: data?.comments.pageInfo.hasNextPage,
    fetchMore: async () => {
      try {
        await fetchMore({
          variables: buildFetchVariables(
            data?.comments.pageInfo.endCursor ?? null,
          ),
          updateQuery: appendCommentsQuery,
        })
      } catch (fetchMoreCommentsError) {
        toast.error('Error fetching more comments')
        throw new Error(
          'Failed to fetch more comments: ' +
            (fetchMoreCommentsError instanceof Error
              ? fetchMoreCommentsError
              : new Error(String(fetchMoreCommentsError))
            ).message,
          { cause: fetchMoreCommentsError },
        )
      }
    },
  })

  return React.useMemo(
    () => ({
      data,
      loading,
      error,
      fetchingMore,
      lastElementRef,
      fetchNewComments,
      retry,
    }),
    [
      data,
      loading,
      error,
      fetchingMore,
      lastElementRef,
      fetchNewComments,
      retry,
    ],
  )
}
